<template>
    <dialog-modal :show="modal" @close="$emit('close', false)">
        <template #title>
            {{ $t("tenants.create.title") }}
        </template>

        <template #content class="bg-gray-50">
            <!--   Airtable_record_id -->
            <airtable-selector
                :errors="errorBags.default ? errorBags.default.airtable_record_id : false"
                type="tenants"
                key-name="name"
                class="my-4"
                @input="handleAirtableRecord"
            />

            <!--   Id    -->
            <div class="my-4">
                <label for="id" class="block text-sm font-medium text-gray-700">
                    {{ $t("tenants.forms.path.title") }}
                </label>
                <div class="mt-1 flex rounded-md shadow-sm" :class="{ 'border border-red-500': errors.id }">
                    <span
                        class="
                            inline-flex
                            items-center
                            px-3
                            rounded-l-md
                            border border-r-0 border-gray-300
                            bg-gray-50
                            text-gray-500
                            sm:text-sm
                        "
                    >
                        {{ env.url }} /
                    </span>
                    <input
                        id="id"
                        type="text"
                        name="id"
                        class="
                            flex-1
                            min-w-0
                            block
                            w-full
                            px-3
                            py-2
                            bg-white
                            rounded-none rounded-r-md
                            focus:ring-indigo-500
                            focus:border-indigo-500
                            sm:text-sm
                            border-gray-300
                        "
                        @input="form.id = $event.target.value"
                    />
                </div>
                <ul v-if="errors.id">
                    <li v-for="error in errorBags.default.id" :key="error" class="text-red-500 text-xs">
                        {{ error }}
                    </li>
                </ul>
            </div>
            <!--   /Id    -->

            <!--   /Image  -->
            <div class="my-4 w-full">
                <label for="file-upload" class="block text-sm font-medium text-gray-700">
                    {{ $t("tenants.forms.logo.title") }}
                </label>
                <div
                    :class="{ 'border-red-500': errors.image }"
                    class="flex border-2 border-gray-300 w-full border-dashed rounded-md"
                    @drop="imageUploadDrop"
                    @dragover="imageUploadDragover"
                >
                    <div class="space-y-1 text-center w-full">
                        <div class="flex text-sm text-gray-600 w-full">
                            <label
                                v-if="!form.image"
                                for="file-upload"
                                class="
                                    relative
                                    w-full
                                    cursor-pointer
                                    bg-white
                                    rounded-md
                                    font-medium
                                    text-indigo-600
                                    hover:text-indigo-500
                                    focus-within:outline-none
                                    focus-within:ring-2
                                    focus-within:ring-offset-2
                                    focus-within:ring-indigo-500
                                    py-6
                                "
                            >
                                <span>{{ $t("tenants.forms.file.title") }}</span>
                                <input
                                    id="file-upload"
                                    name="image"
                                    type="file"
                                    class="sr-only"
                                    accept=".jpg,.jpeg,.png,.gif,.webp"
                                    @change.prevent="uploadImage"
                                />
                                <span class="pb-2 flex flex-col mx-auto">
                                    <svg
                                        class="mx-auto h-12 w-12 text-gray-400"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 48 48"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </span>
                                <p class="pl-1">{{ $t("tenants.forms.file.alt") }}</p>
                                <div class="flex flex-col">
                                    <p class="text-xs text-gray-500">
                                        {{ $t("tenants.forms.file.extensions") }}
                                    </p>
                                </div>
                            </label>
                            <div class="preview-images py-4 flex max-h-48">
                                <img
                                    v-if="imageList"
                                    id="uploaded-image"
                                    class="m-auto max-w-full max-h-full"
                                    :src="imageList"
                                    alt="an uploaded image"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <ul v-if="errors.image">
                    <li v-for="error in errorBags.default.image" :key="error" class="text-red-500 text-xs">
                        {{ error }}
                    </li>
                </ul>
            </div>

            <!--   /Image  -->
        </template>

        <template #footer class="bg-gray-50">
            <div class="flex justify-between">
                <button-secondary button-style="danger" class="mr-auto" @button-click="closeCreate()"
                    >{{ $t("incidents.buttons.cancel") }}
                </button-secondary>

                <button-primary @click="submit">{{ $t("tenants.buttons.new") }}</button-primary>
            </div>
        </template>
    </dialog-modal>
</template>

<script>
import { reactive, ref } from "vue";
import { Inertia } from "@inertiajs/inertia";
import AirtableSelector from "@/Components/AirtableSelector";
import ButtonPrimary from "@/Components/ButtonPrimary";
import ButtonSecondary from "@/Components/ButtonSecondary";
import DialogModal from "@/Jetstream/DialogModal";
import env from "../../env";

export default {
    name: "TenantCreate",
    components: {
        AirtableSelector,
        ButtonPrimary,
        ButtonSecondary,
        DialogModal,
    },
    props: {
        errorBags: Object,
        errors: Object,
        modal: [Object, Boolean],
    },
    emits: ["close"],
    // eslint-disable-next-line vue/no-setup-props-destructure
    setup(props, { emit }) {
        const form = reactive({
            id: null,
            image: null,
            airtable_record_id: null,
        });

        const imageList = ref(null);

        const submit = () => {
            Inertia.post(route("universal.tenants.store"), form, {
                forceFormData: true,
                // eslint-disable-next-line vue/no-mutating-props
                onSuccess: () => emit("close", false),
            });
        };

        const imageUploadDrop = (event) => {
            event.preventDefault();
            const fileReader = new FileReader();
            fileReader.onload = () => {
                imageList.value = fileReader.result;
            };
            fileReader.readAsDataURL(event.dataTransfer.files[0]);
            form.image = event.dataTransfer.files[0];
        };

        const uploadImage = (event) => {
            const fileReader = new FileReader();
            fileReader.onload = () => {
                imageList.value = fileReader.result;
            };
            fileReader.readAsDataURL(event.target.files[0]);
            form.image = event.target.files[0];
        };
        const imageUploadDragover = (event) => {
            event.preventDefault();
        };

        const handleAirtableRecord = (value) => {
            form.airtable_record_id = value;
        };

        return {
            form,
            submit,
            handleAirtableRecord,
            env,
            imageUploadDrop,
            uploadImage,
            imageUploadDragover,
            imageList,
        };
    },
};
</script>
